/**
 * Pure H OS · Sprint 12 · Core mocks — types + live views.
 * Payload now lives in MongoDB `pureh_companies`, `pureh_workspaces`,
 * `pureh_departments`, `pureh_executive_dashboard_data`, `pureh_demo_user`.
 */
import { liveArray, liveSingleton } from "../services/store";

// ── Company / Workspace / Session ───────────────────────────────
export type Company = {
  id: string;
  name: string;
  kind: "holding" | "operating";
  color: string;
  workspaceCount: number;
};
export type Workspace = {
  id: string;
  companyId: string;
  name: string;
  brand: string;
  currency: string;
  industry: string;
};
export type SessionUser = {
  id: string;
  name: string;
  email: string;
  role: "owner" | "executive" | "manager" | "Owner" | "Admin" | "Manager" | "Employee";
  avatar?: string;
  avatarInitials?: string;
  title?: string;
  isHoldingOwner?: boolean;
};

export const DEMO_USER: SessionUser = liveSingleton<SessionUser>(
  "demo_user",
  { id: "", name: "", email: "", role: "owner", avatarInitials: "" },
);

export const COMPANIES: Company[] = liveArray<Company>("companies");
export const WORKSPACES: Workspace[] = liveArray<Workspace>("workspaces");

// ── Departments ─────────────────────────────────────────────────
export type Department = {
  key: string;
  name: string;
  lead: string;
  headcount: { human: number; ai: number };
  focus: string;
};
export const DEPARTMENTS: Department[] = liveArray<Department>("departments");

// ── Executive Dashboard payload ─────────────────────────────────
export type Kpi = { key: string; label: string; value: string; delta: number; trend: "up" | "down" | "flat"; hint?: string };
export type MorningBriefItem = { id: string; headline: string; body: string; tag: "risk" | "opportunity" | "insight" };
export type AttentionItem = { id: string; title: string; detail: string; severity: "high" | "medium" | "low"; dueRelative: string; link?: string };
export type Decision = { id: string; title: string; context: string; requiredBy: string; owner: string };
export type DepartmentHealth = { key: string; name: string; score: number; headline: string; trend: "up" | "down" | "flat" };
export type RevenuePoint = { month: string; revenue: number; target: number };
export type AiDiscovery = { id: string; title: string; body: string; confidence: number };
export type AiRecommendation = { id: string; title: string; action: string; impact: string; effort: "S" | "M" | "L" };
export type ActivityEvent = { id: string; actor: string; verb: string; target: string; timestamp: string; channel: "system" | "human" | "ai" };

export type ExecutiveDashboardData = {
  contextHeadline: string;
  contextSubline: string;
  kpis: Kpi[];
  morningBrief: MorningBriefItem[];
  attention: AttentionItem[];
  decisions: Decision[];
  departments: DepartmentHealth[];
  revenue: RevenuePoint[];
  discoveries: AiDiscovery[];
  recommendations: AiRecommendation[];
  activity: ActivityEvent[];
};

export const EXECUTIVE_DASHBOARD_DATA: ExecutiveDashboardData = liveSingleton<ExecutiveDashboardData>(
  "executive_dashboard_data",
  {
    contextHeadline: "",
    contextSubline: "",
    kpis: [],
    morningBrief: [],
    attention: [],
    decisions: [],
    departments: [],
    revenue: [],
    discoveries: [],
    recommendations: [],
    activity: [],
  },
);
